/**
 * **New bundle**, from screen 1d and from the bundle list.
 *
 * A bundle is created empty and filled in its editor: `PUT /bundles/:id/courses`
 * states the whole order at once, and a create form that also took courses
 * would be the only place in the app where order is expressed twice.
 */

import { useState } from 'react';
import { errorText, teach } from '../api';
import { ds } from '../ds';
import { ErrorNote } from './Chrome';

/** The palette the design draws bundle tiles from. */
export const BUNDLE_COLOURS = [
  'var(--violet-500)',
  'var(--indigo-500)',
  'var(--teal-500)',
  'var(--mauve-500)',
  'var(--slate-500)'
];

export function NewBundleDialog({
  onClose,
  onCreated
}: {
  onClose: () => void;
  /** The new bundle's id — the caller sends the teacher to its editor. */
  onCreated: (id: string) => void;
}) {
  const { Button, Dialog, Input, Switch } = ds();
  const [title, setTitle] = useState('');
  const [colour, setColour] = useState(BUNDLE_COLOURS[0]!);
  const [ordered, setOrdered] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const bundle = await teach.createBundle({ title: title.trim(), colour, ordered });
      onCreated(bundle.id);
    } catch (err) {
      setError(errorText(err, 'Could not create the bundle.'));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      position="fixed"
      width={460}
      title="New bundle"
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" size="md" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={busy || title.trim().length === 0}
            onClick={() => void create()}
          >
            Create
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 16 }}>
        <ErrorNote>{error}</ErrorNote>

        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Onboarding"
        />

        <div style={{ display: 'grid', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>Colour</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {BUNDLE_COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Colour ${BUNDLE_COLOURS.indexOf(c) + 1}`}
                aria-pressed={colour === c}
                onClick={() => setColour(c)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  background: c,
                  border: colour === c ? '2px solid var(--text-strong)' : '2px solid transparent'
                }}
              />
            ))}
          </div>
        </div>

        <Switch
          label="Courses unlock in order"
          checked={ordered}
          onChange={(next) => setOrdered(next)}
        />
      </div>
    </Dialog>
  );
}
